import React, { useState, useEffect, Fragment, useCallback, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { useTimeoutFn } from 'react-use';
import useApi from '../api';
import Agent from './Agent';
import { v4 as uuidv4 } from 'uuid';
import { MessageType } from '../types';
import { conLog } from './utils/conLog';
import { loadFont } from './fontLoader';

interface EmbedAgentProps {
  isDraft?: boolean;
  agentId: string;
  config: any;
  parentUrl: string;
  initData: InitializationData;
}

interface InitializationData {
  lang: string;
  queryParams: string;
  deviceInfo: {
    isMobile: boolean;
    isTablet: boolean;
    isLaptop: boolean;
    screenWidth: number;
    screenHeight: number;
  };
}

interface newMessage {
  role: string;
  content: string;
}

interface TaskData {
  taskId: string;
  taskName: string;
  taskDescription: string;
  taskSteps: string[];
}

const EmbedAgent: React.FC<EmbedAgentProps> = ({ isDraft, agentId, config, parentUrl, initData }) => {
  let [isOpen, setIsOpen] = useState(false)
  const { get, getStream, post, postStream, put, patch, remove } = useApi();
  const [links, setLinks] = useState<any>(undefined);
  const [starters, setStarters] = useState<any>(undefined);
  const [conversationId, setConversationId] = useState<any>(undefined);
  const [leadId, setLeadId] = useState<any>(undefined);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [welcomeMessage, setWelcomeMessage] = useState<string>('Welcome! How can I help you today?');
  const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
  const [draftAgentId, setDraftAgentId] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState('');
  const [currentPageOnly, setCurrentPageOnly] = useState(false);
  const [message, setMessage] = useState<newMessage | undefined>(undefined);
  const [aimdocReady, setAimdocReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const parentUrlRef = useRef(parentUrl);
  parentUrlRef.current = parentUrl;

  function closeModal() {
    window.parent.postMessage({ type: 'widgetCollapsed', divType: 'chat' }, '*');
    setIsOpen(false);
  }

  const clickEventRef = useRef({
    lastClickedElement: null as string | null,
    lastClickTime: 0,
    conversationId: undefined as string | undefined,
  });

  function openModal() {
    setIsOpen(true);
    if (localStorage.getItem('aimdocAutoOpen') === 'false') {
      localStorage.setItem('aimdocAutoOpen', 'true');
    }
    window.parent.postMessage({ type: 'widgetOpened', divType: 'chat' }, '*');
  }

  const checkIfMobile = () => {
    return window.innerWidth < 1025;
  };

  function WidgetMessage() {
    const animationInitializedRef = useRef(false);
    let [isShowing, setIsShowing] = useState(false);
    let [isInputShowing, setIsInputShowing] = useState(false);
    let [isPopupShowing, setIsPopupShowing] = useState(false);
    const [input, setInput] = useState('');
    
    useEffect(() => {
      if (animationInitializedRef.current) {
        return;
      }
      
      animationInitializedRef.current = true;
      
      const iconTimer = setTimeout(() => {
        setIsShowing(true);
      }, 500);
      
      const inputTimer = setTimeout(() => {
        setIsInputShowing(true);
      }, 500);
      
      const popupTimer = setTimeout(() => {
        setIsPopupShowing(true);
      }, 500);
      
      return () => {
        clearTimeout(iconTimer);
        clearTimeout(inputTimer);
        clearTimeout(popupTimer);
      };
    }, []);

    const trackWidgetClick = (element: string, elementText: string, elementType: string = 'interaction') => {
      const clickData = {
        x: 0,
        y: 0,
        timestamp: new Date().toISOString(),
        element: element,
        element_name: `aimdoc_widget_${elementType}`,
        element_text: elementText,
        parent_window_url: parentUrl,
        source: 'widget'
      };
      handleClickEvent(clickData);
    };

    const closePopup = () => {
      trackWidgetClick('BUTTON', 'Close Popup');
      localStorage.setItem('aimdocPopupClosed', 'true');
      setTimeout(() => {
        window.parent.postMessage({ type: 'setIframeWidth', divType: 'chat', width: 100 }, '*');
      }, 300);
      setIsPopupShowing(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (input.trim()) {
        setMessage({
          role: 'user',
          content: input
        });
        openModal();
      }
    };

    useEffect(() => {
      if (localStorage.getItem('aimdocPopupClosed') === 'true') {
        setTimeout(() => {
          window.parent.postMessage({ type: 'setIframeWidth', divType: 'chat', width: 100 }, '*');
        }, 300);
      }
    }, [localStorage.getItem('aimdocPopupClosed')]);

    return (
      <div className="space-y-2">
        {config.popup_message_enabled && config.popup_message !== '' && !localStorage.getItem('aimdocPopupClosed') && (
          <div className="mr-2 ml-8 hover:cursor-pointer">
            <div className="">
              <Transition
                as={Fragment}
                show={isPopupShowing}
                enter="transform transition duration-[400ms]"
                enterFrom="opacity-0 translate-x-[100%]"
                enterTo="opacity-100 translate-x-0"
                leave="transform duration-200 transition ease-in-out"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo="opacity-0 translate-x-[100%]"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex justify-end">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        trackWidgetClick('DIV', 'Popup Message Click');
                        openModal();
                      }}
                      className="flex rounded-2xl border shadow-sm p-2 ml-2 w-fit bg-white relative hover:scale-105 transition-all duration-200"
                      style={{
                        borderColor: `${config.color}20`,
                        borderWidth: '1px',
                        borderStyle: 'solid'
                      }}
                    >
                      <div
                        className="text-wrap text-sm break-words"
                      >
                        {config.popup_message}
                      </div>
                      <div onClick={(e) => {
                        e.stopPropagation();
                        closePopup();
                      }}
                        className="flex h-6 w-6 absolute -top-3 -left-3 rounded-full items-center justify-center text-xs hover:cursor-pointer"
                        style={{
                          backgroundColor: `${config.color}`,
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-3 w-3 stroke-white">
                          <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {starters && starters.length > 0 && !initData?.deviceInfo?.isMobile && (
                    <div className="flex flex-col items-end gap-2">
                      {starters.map((starter: any) => (
                        <div
                          key={starter.id}
                          onClick={() => {
                            trackWidgetClick('BUTTON', `Starter: ${starter.value}`);
                            if (!isOpen) {
                              setMessage({
                                role: 'user',
                                content: starter.value
                              });
                            }
                            openModal();
                          }}
                          className="rounded-2xl w-fit ml-10 shadow-sm p-2 bg-white text-xs transition-all duration-200 hover:-translate-x-1"
                          style={{
                            borderColor: `${config.color}20`,
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            const r = parseInt(config.color.slice(1, 3), 16);
                            const g = parseInt(config.color.slice(3, 5), 16);
                            const b = parseInt(config.color.slice(5, 7), 16);
                            const lightR = Math.round(r * 0.05 + 255 * 0.95);
                            const lightG = Math.round(g * 0.05 + 255 * 0.95);
                            const lightB = Math.round(b * 0.05 + 255 * 0.95);
                            e.currentTarget.style.backgroundColor = `rgb(${lightR}, ${lightG}, ${lightB})`;
                            e.currentTarget.style.borderColor = `${config.color}30`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.borderColor = `${config.color}20`;
                          }}
                        >
                          {starter.value}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Transition>
            </div>
          </div>
        )}
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-2">
            <Transition
              as="div"
              show={isInputShowing}
              enter="transform transition duration-[400ms]"
              enterFrom="opacity-0 translate-y-10"
              enterTo="opacity-100 translate-y-0"
            >
              {!localStorage.getItem('aimdocPopupClosed') && (
                <form onSubmit={handleSubmit} className="flex max-w-[200px] sm:max-w-none">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={config?.search_bar_placeholder}
                    className="w-full px-2 rounded-2xl shadow-md focus:outline-none relative border-transparent bg-gradient-to-r from-transparent to-transparent bg-clip-padding"
                    style={{
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      backgroundImage: `
      linear-gradient(white, white),
      linear-gradient(var(--angle, 0deg), ${config.color}, transparent 50%, ${config.color})
    `,
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                      border: '1px solid transparent',
                      animation: 'rotate 12s linear infinite',
                      ['--angle' as string]: '0deg',
                    }}
                  />
                </form>
              )}
            </Transition>

            <div className="aimdoc-widget-button h-12 w-12">
              <Transition
                as={Fragment}
                show={isShowing}
                enter="transform transition duration-[600ms]"
                enterFrom="opacity-0 rotate-[-120deg] scale-50"
                enterTo="opacity-100 rotate-0 scale-100"
                leave="transform duration-200 transition ease-in-out"
                leaveFrom="opacity-100 rotate-0 scale-100 "
                leaveTo="opacity-0 scale-95 "
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    trackWidgetClick('BUTTON', 'Open Aimdoc');
                    openModal();
                  }}
                  className="aimdoc-widget-icon h-full w-full rounded-full shadow-xl transition hover:scale-105 hover:cursor-pointer flex items-center justify-center"
                  style={{ backgroundColor: config.color }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="" className="fill-white w-5 h-5">
                    <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97-1.94.284-3.916.455-5.922.505a.39.39 0 0 0-.266.112L8.78 21.53A.75.75 0 0 1 7.5 21v-3.955a48.842 48.842 0 0 1-2.652-.316c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" />
                  </svg>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const checkOrSetUserId = async () => {
    let userId = localStorage.getItem('aimdocUserId');
    if (!userId) {
      userId = uuidv4();
      if (userId) {
        localStorage.setItem('aimdocUserId', userId);
      }
    } else {
      try {
        const { data } = await post(`/latest-session/`, {
          visitor_id: userId,
          agent_id: agentId,
        });
        setConversationId(data.id);
        setMessages(data.messages);
        setLeadId(data.lead.id);
      } catch (error) {
      }
    }
    if (userId) {
      setVisitorId(userId);
    }
    return userId;
  };

  useEffect(() => {
    if (config) {
      setAimdocReady(true);

      if (config.font) {
        try {
          loadFont(config.font).catch(err => {
            console.warn('Font loading failed, using fallback:', err);
          });
        } catch (err) {
          console.warn('Font loading failed, using fallback:', err);
        }
      }

      setLinks(config.links);
      setStarters(config.starters);
      setWelcomeMessage(config.welcome_message);
      setCurrentPage(config.current_page);
      setCurrentPageOnly(config.current_page_only);

      if (config.draft_agent_id) {
        setDraftAgentId(config.draft_agent_id);
      }

      if (config.auto_open_enabled) {
        if (config.open_once_per_session && localStorage.getItem('aimdocAutoOpen') === 'true') {
          return;
        }
        setTimeout(() => {
          openModal();
          localStorage.setItem('aimdocAutoOpen', 'true');
        }, config.auto_open_seconds * 1000);
      }

      setConfigReady(true);

      const initUserId = async () => {
        const userId = await checkOrSetUserId();
      };
      initUserId();
    }
  }, [config]);

  useEffect(() => {
    const fetchUserId = async () => {
      const userId = await checkOrSetUserId();
      conLog('User ID:', userId);
    };
    if (!isOpen) {
      fetchUserId();
    }
  }, [isOpen]);

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleClickEvent = (clickData: any) => {
    const clickEventId = uuidv4();
    clickData.id = clickEventId;

    if (conversationId) {
      post(`/sessions/${conversationId}/process-click-event/`, clickData).then((response) => {
        if (response.data.message === "Click event processed successfully" ||
          response.data.message === "Click event already processed") {
          localStorage.removeItem('aimdocEvents');
          window.parent.postMessage('clearClickEvents', '*');
        } else {
          console.warn('Unexpected response from server:', response.data);
        }
      }).catch((error) => {
        console.error('Error processing click event:', error);
      });
    } else {
      console.warn('ConversationId or visitorId not available, storing event locally');
      window.parent.postMessage({ type: 'storeClickEvent', data: clickData }, '*');
      let aimdocEvents = JSON.parse(localStorage.getItem('aimdocEvents') || '[]');
      aimdocEvents.push(clickData);
      localStorage.setItem('aimdocEvents', JSON.stringify(aimdocEvents));
    }
  };

  const processClickEvent = useCallback((clickData: any) => {
    handleClickEvent(clickData);
  }, [conversationId, post]);

  const debouncedProcessClickEvent = useCallback(
    debounce(processClickEvent, 1000),
    [processClickEvent]
  );

  useEffect(() => {
    const handleClickEvent = (event: MessageEvent) => {
      if (event.data.type === 'newClickEvent') {
        const clickData = event.data.data;
        const currentTime = Date.now();

        if (clickData.element_id === clickEventRef.current.lastClickedElement && currentTime - clickEventRef.current.lastClickTime < 300) {
          clickData.timestamp = new Date().toISOString();
        } else {
          clickEventRef.current.lastClickedElement = clickData.element_id;
          clickEventRef.current.lastClickTime = currentTime;
          debouncedProcessClickEvent(clickData);
        }
      }
    };

    window.addEventListener('message', handleClickEvent);

    return () => {
      window.removeEventListener('message', handleClickEvent);
    };
  }, [debouncedProcessClickEvent]);

  useEffect(() => {
    window.parent.postMessage({ type: 'getClickEvents' }, '*');
    const handleMessage = (event: any) => {
      if (event.data.type === 'clickEvents') {
        localStorage.setItem('aimdocEvents', JSON.stringify(event.data.data));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    clickEventRef.current.conversationId = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (!contentRef.current) return;

    const contentElement = contentRef.current;
    let resizeTimeout: NodeJS.Timeout;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const height = entry.contentRect.height;

        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }

        resizeTimeout = setTimeout(() => {
          if (!isOpen) {
            const adjustedHeight = height + 40;
            window.parent.postMessage({ type: 'setIframeHeight', divType: 'chat', height: adjustedHeight }, '*');
          }
        }, 0);
      }
    });

    resizeObserver.observe(contentElement);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    @property --angle {
      syntax: '<angle>';
      initial-value: 0deg;
      inherits: false;
    }

    @keyframes rotate {
      from {
        --angle: 0deg;
      }
      to {
        --angle: 360deg;
      }
    }
  `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [config.color]);

  const checkUnauthorizedUse = (url: string) => {
    const unauthorizedDomains = ['technoparticles.com'];
    try {
      const urlObj = new URL(url);
      return unauthorizedDomains.some(domain =>
        urlObj.hostname === domain ||
        urlObj.hostname.endsWith(`.${domain}`)
      );
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const handleStoredResults = (event: MessageEvent) => {
      if (event.data.type === 'aimdocStoredActionResults') {
        const storedResults = event.data.data;
        if (storedResults && storedResults.length > 0) {
          const formattedResults = storedResults
            .map((result: any) => result.message)
            .join('\n\n');

          if (aimdocReady && configReady) {
            setMessage({
              role: 'system',
              content: formattedResults
            });
            window.parent.postMessage({ type: 'aimdocClearActionResults' }, '*');
            openModal();
          }
        }
      }
    };

    if (aimdocReady && configReady) {
      window.addEventListener('message', handleStoredResults);
      window.parent.postMessage({ type: 'aimdocGetActionResults' }, '*');
    }

    return () => {
      window.removeEventListener('message', handleStoredResults);
    };
  }, [aimdocReady, configReady]);

  useEffect(() => {
    const handleTaskMessage = (event: MessageEvent) => {
      if (event.data.type === 'aimdocStartTask') {
        const task = event.data.data;

        if (!isOpen) {
          openModal();
        }

        setMessage({
          role: 'system',
          content: `${task.taskName}\n\nDescription: ${task.taskDescription}\n\nSteps:\n${task.taskSteps.map((step: string, index: number) => `${index + 1}. ${step}`).join('\n')}`
        });
      }
    };

    window.addEventListener('message', handleTaskMessage);
    return () => {
      window.removeEventListener('message', handleTaskMessage);
    };
  }, [isOpen]);

  if (parentUrl && checkUnauthorizedUse(parentUrl)) {
    return (
      <div className="fixed z-[100000000] bottom-5 right-5 max-w-[400px] bg-white rounded-lg shadow-xl p-6 border border-red-500">
        <div className="text-lg font-bold text-red-600 mb-3">⚠️ Unauthorized Usage Alert</div>
        <p className="text-gray-800 mb-4">
          This website is illegally using Aimdoc's AI technology without authorization.
        </p>
        <p className="text-gray-800 mb-4">
          To check out our actual product, please visit:
        </p>
        <a
          href="https://aimdoc.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          https://aimdoc.ai
        </a>
      </div>
    );
  }

    return (
    config && agentId && (
      <div ref={contentRef} className={`${config.font} fixed z-[2147483647] bottom-5 right-5 embed w-full`} style={{ backgroundColor: 'transparent' }}>
        {config && !isOpen && (
          <div className="flex flex-col" style={{ backgroundColor: 'transparent' }}>
            <WidgetMessage />
          </div>
        )}
        {config && (
          <Transition appear show={isOpen} as={Fragment}>
            <div className="container relative z-[2147483647] h-full" style={{ backgroundColor: 'transparent' }}>
              <Transition.Child
                as={Fragment}
              >
                <div className="fixed bg-opacity-50" style={{ backgroundColor: 'transparent' }} />
              </Transition.Child>

              <div className={`fixed inset-0 w-full transition-all duration-200`} style={{ backgroundColor: 'transparent' }}>
                <div className="flex h-full justify-end" style={{ backgroundColor: 'transparent' }}>
                  <div className={`flex items-center justify-center w-full h-full text-center transition-all duration-200`} style={{ backgroundColor: 'transparent' }}>
                    <Transition.Child
                      as={Fragment}
                      enter="duration-[200ms] ease-in"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="ease-out duration-[200ms]"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <div className={`w-full h-full transform overflow-hidden text-left align-middle shadow-xl transition-all ${initData?.deviceInfo?.isMobile ? '' : 'rounded-3xl'}`} style={{ backgroundColor: 'transparent' }}>
                        {visitorId && (
                          <Agent
                            agentId={agentId}
                            closeModal={closeModal}
                            sidebar={true}
                            setSidebar={() => { }}
                            embedConfig={config}
                            agentLinks={links}
                            agentStarters={starters}
                            lastConvId={conversationId}
                            setConvId={setConversationId}
                            lastMessages={messages}
                            existLeadId={leadId}
                            visitorId={visitorId}
                            welcomeMessage={welcomeMessage}
                            queryParams={initData?.queryParams}
                            parentUrl={parentUrl}
                            draftAgentId={draftAgentId}
                            currentPageOnly={config.current_page_only}
                            currentPage={currentPage}
                            messageInput={message}
                            setMessageInput={setMessage}
                            lang={initData?.lang}
                            isMobile={initData?.deviceInfo?.isMobile}
                          />
                        )}
                      </div>
                    </Transition.Child>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        )}
      </div>
    )
  );
};

export default EmbedAgent;
